import { Download, Trash2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/cn';
import { type LinkLike, NativeLink } from '../../lib/link';

type WidgetTone = 'accent' | 'success' | 'purple' | 'warn' | 'danger' | 'muted' | 'primary';

const TONE_VAR: Record<WidgetTone, string> = {
	accent: 'var(--color-accent)',
	success: 'var(--color-accent-green)',
	purple: 'var(--color-accent-purple)',
	warn: 'var(--color-warn)',
	danger: 'var(--color-danger)',
	muted: 'var(--color-text-muted)',
	primary: 'var(--color-text-primary)',
};

export type { WidgetTone };
export { TONE_VAR };

const GRID_CLASS = {
	2: 'g2',
	3: 'g3',
	4: 'g4',
	5: 'g5',
	'2:1': 'g21',
	lead: 'glead',
} as const;

export interface CardGridProps {
	/**
	 * Colonne della griglia dashboard: un numero per colonne uguali (legacy
	 * `g2`/`g3`/`g4`/`g5`), `'2:1'` per il grafico affiancato al pannello
	 * laterale (legacy `g21`), `'lead'` per la riga aperta da una card più larga
	 * delle altre (legacy `glead`).
	 */
	columns: 2 | 3 | 4 | 5 | '2:1' | 'lead';
	/** Card figlie della griglia. */
	children: React.ReactNode;
}

/** Griglia standard di card della dashboard (legacy `g2`/`g3`/`g4`/`g5`/`g21`/`glead`). */
const CardGrid = ({ columns, children }: CardGridProps) => (
	<div className={GRID_CLASS[columns] ?? GRID_CLASS[2]}>{children}</div>
);
CardGrid.displayName = 'CardGrid';

export interface KpiBigBar {
	/** Riempimento 0-100. */
	pct: number;
	/** Colore del riempimento. Default: `purple` (l'`accent2` del mock). */
	tone?: WidgetTone;
	/** Cosa rappresenta la barra, per gli screen reader. */
	label?: string;
}

export interface KpiBigProps {
	/** CalcTrace o altro elemento renderizzato sopra il numero. */
	trace?: React.ReactNode;
	/** Valore numerico principale, in grande. */
	value: React.ReactNode;
	/**
	 * Colore del valore.
	 *
	 * Oggi non si vede: la regola «colore uniforme dei valori» in
	 * `styles/legacy-utilities.css` riporta ogni numero all'accento secondario,
	 * come fa il mock v11. Resta dichiarato perché è il tono *semantico* del KPI —
	 * è l'informazione che serve se un domani si torna ai colori per card, e non
	 * si ricostruisce guardando il rendering.
	 */
	tone?: WidgetTone;
	/** Unità di misura mostrata sotto il valore. Sempre attenuata (vedi `tone`). */
	unit?: React.ReactNode;
	/**
	 * Barra sottile sotto il numero: dà la proporzione del KPI rispetto alla
	 * grandezza di riferimento della card (es. quanto della produzione è
	 * condiviso). Sta tra numero e trend, come nel mock.
	 */
	bar?: KpiBigBar;
	/** Testo di variazione/trend mostrato in fondo. */
	trend?: React.ReactNode;
	/** Colore del trend. Anch'esso uniformato ad attenuato dal mock: vedi `tone`. */
	trendTone?: WidgetTone | 'up' | 'down';
	/** Riga attenuata in coda, separata da un filetto (es. l'ultimo dato consolidato). */
	footer?: React.ReactNode;
	/** Rimando in fondo alla card, in accento: apre l'approfondimento del numero. */
	action?: { label: React.ReactNode; onClick: () => void };
	/** Trattamento visivo più grande (legacy `kpi-hero`). */
	hero?: boolean;
}

/** Blocco numero KPI in grande dentro una PanelCard (legacy `kpi-big`). */
const KpiBig = ({
	trace,
	value,
	tone = 'accent',
	unit,
	bar,
	trend,
	trendTone,
	footer,
	action,
	hero,
}: KpiBigProps) => (
	<div className={cn('kpi-big', hero && 'kpi-hero')}>
		{trace}
		<div className="number" style={{ color: TONE_VAR[tone] }}>
			{value}
		</div>
		{unit && <div className="unit">{unit}</div>}
		{bar && (
			<div
				className="mt-[9px] h-1.5 overflow-hidden rounded-[3px]"
				style={{ background: 'var(--color-border)' }}
				// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- barra disegnata in CSS, senza sorgente raster: role + aria-label è il pattern accessibile
				role="img"
				aria-label={bar.label ?? `${Math.round(bar.pct)}%`}
			>
				<div
					className="h-full rounded-[3px]"
					style={{
						width: `${Math.max(0, Math.min(100, bar.pct))}%`,
						background: TONE_VAR[bar.tone ?? 'purple'],
					}}
				/>
			</div>
		)}
		{trend && (
			<div
				className={cn('trend', trendTone === 'up' && 'up', trendTone === 'down' && 'down')}
				style={
					trendTone && trendTone !== 'up' && trendTone !== 'down'
						? { color: TONE_VAR[trendTone] }
						: undefined
				}
			>
				{trend}
			</div>
		)}
		{footer && (
			<div className="mt-2 border-t border-border pt-2 text-[11px] text-text-muted">{footer}</div>
		)}
		{action && (
			<button
				type="button"
				onClick={action.onClick}
				className="mt-[7px] flex items-center gap-[5px] text-[11px] font-bold text-accent hover:underline"
			>
				{action.label}
			</button>
		)}
	</div>
);
KpiBig.displayName = 'KpiBig';

export interface ProgressItemProps {
	/** Etichetta della barra. */
	label: React.ReactNode;
	/** Valore mostrato a destra dell'etichetta. */
	valueLabel?: React.ReactNode;
	/** Colore del riempimento. Default: `accent`. */
	tone?: WidgetTone;
	/** Percentuale di riempimento (0-100). */
	pct: number;
	/** Se true, usa un gradiente successo→accento invece del colore piatto di `tone`. */
	gradient?: boolean;
	/** Nota mostrata sotto la barra. */
	hint?: React.ReactNode;
	/**
	 * Senza il margine di coda: è la variante che il mock v11 usa quando le barre
	 * sono un elenco fitto (le sette variabili del Meteo Live) e non voci
	 * distinte, e per l'ultima barra di ogni card.
	 */
	tight?: boolean;
}

/** Barra di avanzamento con etichetta (legacy `prog-item`). */
const ProgressItem = ({
	label,
	valueLabel,
	tone = 'accent',
	pct,
	gradient,
	hint,
	tight,
}: ProgressItemProps) => (
	<div className={cn('prog-item', tight && 'mb-0')}>
		<div className="prog-head">
			<span>{label}</span>
			{valueLabel != null && <span style={{ color: TONE_VAR[tone] }}>{valueLabel}</span>}
		</div>
		<div className="prog-bar">
			<div
				className="prog-fill"
				style={{
					width: `${Math.max(0, Math.min(100, pct))}%`,
					background: gradient
						? 'linear-gradient(90deg,var(--color-accent-green),var(--color-accent))'
						: TONE_VAR[tone],
				}}
			/>
		</div>
		{hint && <div className="prog-hint">{hint}</div>}
	</div>
);
ProgressItem.displayName = 'ProgressItem';

export interface AiBoxProps {
	/** Contenuto del box (tipicamente un suggerimento/insight generato dall'AI). */
	children: React.ReactNode;
	/** Se true, applica margini laterali/inferiori (box annidato dentro un'altra card). */
	inset?: boolean;
}

/** Box evidenziato per insight/annotazioni AI (legacy `ai-box`). */
const AiBox = ({ children, inset }: AiBoxProps) => (
	<div className={cn('ai-box', inset && 'mx-3 mb-3')}>{children}</div>
);
AiBox.displayName = 'AiBox';

export interface SectionHeadProps {
	/** Testo dell'intestazione. */
	children: React.ReactNode;
}

/** Intestazione di colonna tra le card di una dashboard (legacy `section-head`). */
const SectionHead = ({ children }: SectionHeadProps) => (
	<div className="section-head">{children}</div>
);
SectionHead.displayName = 'SectionHead';

export type TagTone = 'green' | 'blue' | 'purple' | 'orange' | 'done' | 'warn';

const TAG_CLS: Record<TagTone, string> = {
	green: 'badge-green',
	blue: 'badge-blue',
	purple: 'badge-purple',
	orange: 'badge-orange',
	done: 'tag-done',
	warn: 'tag-warn',
};

export interface TagProps {
	/** Colore/stile del tag. */
	tone: TagTone;
	/** Testo del tag. */
	children: React.ReactNode;
}

/** Etichetta di stato inline (legacy `tag badge-*` / `tag-done`). */
const Tag = ({ tone, children }: TagProps) => (
	<span className={cn('tag', TAG_CLS[tone])}>{children}</span>
);
Tag.displayName = 'Tag';

export interface ReportCardRowProps {
	/**
	 * Icona del report. Opzionale: la pagina Report del mock v11
	 * (`SLIMS-CER_v11.html`, `#s-report`) non mostra icone sulle righe
	 * "Adempimenti"/"Report generati" — solo alcuni altri usi la portano.
	 */
	icon?: React.ReactNode;
	/** Titolo del report. */
	title: React.ReactNode;
	/** Testo secondario (es. data, autore). */
	sub: React.ReactNode;
	/** Testo di stato mostrato nel tag a destra. */
	status: React.ReactNode;
	/** Colore del tag di stato. */
	statusTone: TagTone;
	/** Se true, attenua la riga (report già gestito/archiviato). */
	dimmed?: boolean;
	/**
	 * Se presente, la parte testuale della riga diventa un pulsante: clic =
	 * apri il report (tipicamente in una nuova finestra).
	 */
	onOpen?: () => void;
	/** Se presente, aggiunge un pulsante icona a destra per scaricare il report. */
	onDownload?: () => void;
	/** Se presente, aggiunge un pulsante icona a destra per eliminare il report. */
	onDelete?: () => void;
}

/** Riga di un elenco report (legacy `report-card`). */
const ReportCardRow = ({
	icon,
	title,
	sub,
	status,
	statusTone,
	dimmed,
	onOpen,
	onDownload,
	onDelete,
}: ReportCardRowProps) => {
	const body = (
		<>
			{icon != null && <span className="rc-icon">{icon}</span>}
			<div style={{ flex: 1 }}>
				<div className="rc-title">{title}</div>
				<div className="rc-sub">{sub}</div>
			</div>
		</>
	);
	return (
		<div className="report-card" style={dimmed ? { opacity: 0.7 } : undefined}>
			{onOpen ? (
				<button
					type="button"
					onClick={onOpen}
					className="flex min-w-0 flex-1 items-center gap-2.5 border-0 bg-transparent p-0 text-left text-inherit"
				>
					{body}
				</button>
			) : (
				body
			)}
			<span className={cn('rc-status', TAG_CLS[statusTone])}>{status}</span>
			{onDownload && (
				<button
					type="button"
					onClick={onDownload}
					aria-label="Scarica report"
					className="ml-2 inline-flex h-7 w-7 flex-none items-center justify-center rounded-md border border-border text-text-muted transition-colors hover:border-accent hover:text-accent"
				>
					<Download size={14} />
				</button>
			)}
			{onDelete && (
				<button
					type="button"
					onClick={onDelete}
					aria-label="Elimina report"
					className="ml-2 inline-flex h-7 w-7 flex-none items-center justify-center rounded-md border border-border text-text-muted transition-colors hover:border-danger hover:text-danger"
				>
					<Trash2 size={14} />
				</button>
			)}
		</div>
	);
};
ReportCardRow.displayName = 'ReportCardRow';

export interface GseChecklistItemProps {
	/** Stato del controllo di conformità (ok/attenzione/in attesa), determina icona e colore. */
	state: 'ok' | 'warn' | 'pending';
	/** Descrizione del controllo. */
	label: React.ReactNode;
	/** Testo di stato mostrato a destra. */
	status: React.ReactNode;
	/** Colore del testo di stato; se assente viene derivato da `state`. */
	statusTone?: WidgetTone;
}

/** Riga di una checklist di conformità (es. requisiti GSE) (legacy `gse-item`). */
const GseChecklistItem = ({ state, label, status, statusTone }: GseChecklistItemProps) => (
	<div className="gse-item">
		<div
			className={cn(
				'check-icon',
				state === 'ok' ? 'check-ok' : state === 'warn' ? 'check-warn' : 'check-pending',
			)}
		>
			{state === 'ok' ? '✓' : state === 'warn' ? '!' : '·'}
		</div>
		<span className="gse-label">{label}</span>
		<span
			className="gse-status"
			style={{
				color:
					TONE_VAR[
						statusTone ?? (state === 'ok' ? 'success' : state === 'warn' ? 'warn' : 'muted')
					],
			}}
		>
			{status}
		</span>
	</div>
);
GseChecklistItem.displayName = 'GseChecklistItem';

const ACT_ICON_BG: Record<'warn' | 'accent' | 'success' | 'purple', string> = {
	warn: 'rgba(245,158,11,0.2)',
	accent: 'color-mix(in srgb, var(--color-accent) 15%, transparent)',
	success: 'rgba(16,185,129,0.2)',
	purple: 'color-mix(in srgb, var(--color-accent-purple) 20%, transparent)',
};

export interface ActivityItemProps {
	/** Icona dell'attività. */
	icon: React.ReactNode;
	/** Colore di sfondo dell'icona. Default: `accent`. */
	iconTone?: 'warn' | 'accent' | 'success' | 'purple';
	/** Titolo dell'attività/scadenza. */
	title: React.ReactNode;
	/** Testo descrittivo secondario. */
	description?: React.ReactNode;
	/** Testo temporale (es. "2 ore fa", "tra 3 giorni"). */
	time?: React.ReactNode;
	/** Colore del testo temporale. */
	timeTone?: WidgetTone;
	/** Se presente, rende cliccabile il blocco di testo (apre il dettaglio dell'attività). */
	onClick?: () => void;
}

/** Riga di un feed di attività/scadenze (legacy `activity-item`). */
const ActivityItem = ({
	icon,
	iconTone = 'accent',
	title,
	description,
	time,
	timeTone,
	onClick,
}: ActivityItemProps) => {
	const content = (
		<>
			<div className="act-title">{title}</div>
			{description && <div className="act-desc">{description}</div>}
			{time && (
				<div className="act-time" style={timeTone ? { color: TONE_VAR[timeTone] } : undefined}>
					{time}
				</div>
			)}
		</>
	);
	return (
		<div className="activity-item">
			<div className="act-icon" style={{ background: ACT_ICON_BG[iconTone] }}>
				{icon}
			</div>
			{onClick ? (
				<button
					type="button"
					onClick={onClick}
					style={{
						flex: 1,
						minWidth: 0,
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						padding: 0,
						textAlign: 'left',
					}}
				>
					{content}
				</button>
			) : (
				<div>{content}</div>
			)}
		</div>
	);
};
ActivityItem.displayName = 'ActivityItem';

export interface InlineProgressRowProps {
	/** Etichetta a sinistra, su colonna a larghezza fissa perché le barre restino allineate. */
	label: React.ReactNode;
	/** Riempimento 0-100. */
	pct: number;
	/** Colore di barra e conteggio. Default: `accent`. */
	tone?: WidgetTone;
	/**
	 * Colore della sola barra, quando deve differire dal conteggio. Nel mock la
	 * riga dei soci attivi ha la barra nel blu secondario e il numero in verde:
	 * la barra dice «quanti sul totale», il numero dice «e va bene così».
	 */
	barTone?: WidgetTone;
	/** Conteggio a destra. */
	count: React.ReactNode;
}

/**
 * Riga compatta etichetta · barra · conteggio, per le ripartizioni di stato in
 * una card (es. l'avanzamento dell'arruolamento soci).
 *
 * Distinta da {@link ProgressItem}, dove l'etichetta sta *sopra* la barra: qui
 * le tre colonne stanno sulla stessa riga perché servono a confrontare più
 * stati a colpo d'occhio, non a commentarne uno.
 */
const InlineProgressRow = ({
	label,
	pct,
	tone = 'accent',
	barTone,
	count,
}: InlineProgressRowProps) => (
	<div className="flex items-center gap-[9px] py-[5px]">
		<span className="w-[118px] shrink-0 text-[12px] text-text-primary">{label}</span>
		<div
			className="h-1.5 flex-1 overflow-hidden rounded-[3px]"
			style={{ background: 'var(--color-border)' }}
		>
			<div
				className="h-full rounded-[3px]"
				style={{
					width: `${Math.max(0, Math.min(100, pct))}%`,
					background: TONE_VAR[barTone ?? tone],
				}}
			/>
		</div>
		<span
			className="w-6 shrink-0 text-right font-mono text-[12px] font-bold"
			style={{ color: TONE_VAR[tone] }}
		>
			{count}
		</span>
	</div>
);
InlineProgressRow.displayName = 'InlineProgressRow';

/**
 * Le tinte del riquadro icona in una coda. Sono più tenui di quelle di
 * {@link ActivityItem}: lì l'icona è l'unico colore della riga, qui convive col
 * badge del conteggio e a piena intensità farebbero a botte.
 */
const QUEUE_ICON_BG: Record<'warn' | 'accent' | 'success' | 'purple', string> = {
	warn: 'rgba(245,158,11,0.15)',
	accent: 'color-mix(in srgb, var(--color-accent) 12%, transparent)',
	success: 'rgba(16,185,129,0.15)',
	purple: 'color-mix(in srgb, var(--color-accent-purple) 15%, transparent)',
};

export interface QueueRowProps {
	/** Icona nel riquadro colorato a sinistra. */
	icon: React.ReactNode;
	/** Colore di sfondo dell'icona. Default: `accent`. */
	iconTone?: 'warn' | 'accent' | 'success' | 'purple';
	/** Cosa c'è in coda. */
	title: React.ReactNode;
	/** Cosa se ne deve fare. */
	description?: React.ReactNode;
	/** Numero di elementi in coda, nel badge a destra. */
	count: React.ReactNode;
	/** Colore del badge. Default: `blue`. */
	countTone?: TagTone;
	/** Elenco da aprire. Senza, la riga resta informativa. */
	href?: string;
	/** Componente link da usare (default: link nativo `<a>`); passare il `Link` del router per la navigazione client-side. */
	linkComponent?: LinkLike;
}

/** Riga di una coda di lavorazione: icona, cosa c'è da fare, quanti sono. */
const QueueRow = ({
	icon,
	iconTone = 'accent',
	title,
	description,
	count,
	countTone = 'blue',
	href,
	linkComponent: Link = NativeLink,
}: QueueRowProps) => {
	const body = (
		<>
			<div className="inc-icon shrink-0" style={{ background: QUEUE_ICON_BG[iconTone] }}>
				{icon}
			</div>
			<div className="min-w-0 flex-1 text-left">
				<div className="truncate text-[12px] font-bold text-text-primary">{title}</div>
				{description && <div className="text-[10.5px] text-text-muted">{description}</div>}
			</div>
			<span className={cn('card-badge', TAG_CLS[countTone])}>{count}</span>
		</>
	);
	const className =
		'mb-[7px] flex w-full items-center gap-[9px] rounded-lg border border-border bg-card px-2.5 py-2';

	return href ? (
		<Link href={href} className={cn(className, 'transition-colors hover:border-accent/40')}>
			{body}
		</Link>
	) : (
		<div className={className}>{body}</div>
	);
};
QueueRow.displayName = 'QueueRow';

export interface BalanceLineProps {
	/** Voce del bilancio. */
	label: React.ReactNode;
	/** Valore, in evidenza. */
	value: React.ReactNode;
	/** Unità di misura, attenuata accanto al valore. */
	unit?: React.ReactNode;
}

/**
 * Riga di un bilancio energetico: voce a sinistra, valore e unità a destra,
 * separate da un filetto. Da usare dentro {@link BalanceList}.
 */
const BalanceLine = ({ label, value, unit }: BalanceLineProps) => (
	<div className="flex items-baseline justify-between border-b border-border py-[5px]">
		<span className="font-medium text-text-muted">{label}</span>
		<span>
			<strong>{value}</strong>
			{unit != null && <span className="ml-1 text-[11px] font-medium text-text-muted">{unit}</span>}
		</span>
	</div>
);
BalanceLine.displayName = 'BalanceLine';

export interface BalanceListProps {
	/** Righe {@link BalanceLine} e note in coda. */
	children: React.ReactNode;
}

/** Contenitore delle {@link BalanceLine}: fissa scala e interlinea del blocco. */
const BalanceList = ({ children }: BalanceListProps) => (
	<div className="text-[13.5px] font-semibold leading-[1.6] text-text-primary">{children}</div>
);
BalanceList.displayName = 'BalanceList';

export interface MicroLabelProps {
	/** Testo dell'etichetta. */
	children: React.ReactNode;
}

/** Etichetta minuscola maiuscoletta che divide i blocchi dentro una card. */
const MicroLabel = ({ children }: MicroLabelProps) => (
	<div className="mb-1.5 mt-1.5 text-[9.5px] font-extrabold uppercase tracking-[0.5px] text-text-muted">
		{children}
	</div>
);
MicroLabel.displayName = 'MicroLabel';

export interface ChartLegendItem {
	/** Colore del quadratino della voce di legenda. */
	tone: 'accent' | 'purple' | 'success' | 'warn';
	/** Testo della voce. */
	label: React.ReactNode;
	/** Se true, attenua la voce (es. serie nascosta/disattivata). */
	faded?: boolean;
}

const LEGEND_COLOR: Record<ChartLegendItem['tone'], string> = {
	accent: 'var(--color-accent)',
	purple: 'var(--color-accent-purple)',
	success: '#10b981',
	warn: '#f59e0b',
};

export interface ChartLegendProps {
	/** Voci della legenda. */
	items: ChartLegendItem[];
}

/** Riga di legenda a piccoli quadratini colorati sotto un grafico. */
const ChartLegend = ({ items }: ChartLegendProps) => (
	<div className="mt-2 flex gap-3 text-[13px]">
		{items.map((item, i) => (
			<div key={i} className="flex items-center gap-1 text-text-muted">
				<div
					style={{
						width: 8,
						height: 8,
						background: LEGEND_COLOR[item.tone],
						opacity: item.faded ? 0.7 : undefined,
						borderRadius: 2,
					}}
				/>
				{item.label}
			</div>
		))}
	</div>
);
ChartLegend.displayName = 'ChartLegend';

export interface MiniBarProps {
	/** Percentuale di riempimento (0-100). */
	pct: number;
}

/** Barra di percentuale minuscola inline dentro celle di tabella (legacy `mini-bar`). */
const MiniBar = ({ pct }: MiniBarProps) => (
	<div className="mini-bar">
		<div className="mini-fill" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
	</div>
);
MiniBar.displayName = 'MiniBar';

export interface InfoStripProps {
	/** Contenuto della fascia informativa. */
	children: React.ReactNode;
}

/** Fascia informativa verde tenue sotto grafici/tabelle. */
const InfoStrip = ({ children }: InfoStripProps) => (
	<div
		style={{
			marginTop: 8,
			background: 'rgba(16,185,129,0.08)',
			border: '1px solid rgba(16,185,129,0.2)',
			borderRadius: 7,
			padding: '8px 10px',
			fontSize: 13,
		}}
	>
		{children}
	</div>
);
InfoStrip.displayName = 'InfoStrip';

export interface FootnoteProps {
	/** Testo della nota a piè di grafico/tabella. */
	children: React.ReactNode;
}

/** Nota a piè di pagina sotto un grafico o una tabella. */
const Footnote = ({ children }: FootnoteProps) => (
	<p className="mt-2 text-[11px] italic text-text-muted">{children}</p>
);
Footnote.displayName = 'Footnote';

export interface DataTableColumn<Row> {
	/** Chiave univoca della colonna. */
	key: string;
	/** Intestazione della colonna. */
	header: React.ReactNode;
	/** Allineamento del testo. */
	align?: 'left' | 'right' | 'center';
	/** Trattamento tipografico della cella. */
	variant?: 'default' | 'monoAccent' | 'muted';
	/** Renderizza il contenuto della cella per una riga. */
	render: (row: Row, index: number) => React.ReactNode;
}

export interface DataTableProps<Row> {
	/** Definizione delle colonne. */
	columns: DataTableColumn<Row>[];
	/** Righe di dati. */
	rows: Row[];
	/** Estrae una chiave React univoca da una riga. */
	rowKey: (row: Row) => string | number;
	/**
	 * Apre il dettaglio della riga. Quando è presente le righe diventano
	 * interattive — cursore, hover e attivabili da tastiera — così l'esploso per
	 * POD si può agganciare a qualunque tabella energetica senza duplicarla.
	 */
	onRowClick?: (row: Row) => void;
	/** Testo del titolo accessibile delle righe cliccabili, es. 'Apri il dettaglio di'. */
	rowActionLabel?: string;
	/**
	 * Messaggio mostrato in una riga sola (unita su tutte le colonne) quando
	 * `rows` è vuoto — al posto di un `StatusBlock` separato sotto la tabella,
	 * che raddoppiava il riquadro e lasciava vuoto sproporzionato sotto il
	 * testo. Le intestazioni restano visibili come anteprima della forma.
	 */
	emptyMessage?: React.ReactNode;
}

/** Tabella con tema dell'app (legacy `data-table`); componi il contenuto delle celle con Tag/MiniBar/Text. */
function DataTable<Row>({
	columns,
	rows,
	rowKey,
	onRowClick,
	rowActionLabel,
	emptyMessage,
}: DataTableProps<Row>) {
	return (
		<div className="overflow-x-auto">
			<table className="data-table">
				<thead>
					<tr>
						{columns.map((col) => (
							<th
								key={col.key}
								style={col.align && col.align !== 'left' ? { textAlign: col.align } : undefined}
							>
								{col.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 && emptyMessage && (
						<tr>
							<td colSpan={columns.length} className="py-6 text-center text-[13px] text-text-muted">
								{/* Stesso trattamento "a pillola" del messaggio sovrapposto ai
								    grafici vuoti (`SlChart`): coerenza visiva tra tutti gli
								    stati vuoti della pagina. */}
								<span className="inline-block max-w-full rounded-md bg-panel/85 px-3 py-1.5">
									{emptyMessage}
								</span>
							</td>
						</tr>
					)}
					{rows.map((row, i) => (
						<tr
							key={rowKey(row)}
							{...(onRowClick
								? {
										onClick: () => onRowClick(row),
										onKeyDown: (e: React.KeyboardEvent) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												onRowClick(row);
											}
										},
										tabIndex: 0,
										role: 'button',
										title: rowActionLabel,
										style: { cursor: 'pointer' },
									}
								: {})}
						>
							{columns.map((col) => (
								<td
									key={col.key}
									style={{
										...(col.align && col.align !== 'left' ? { textAlign: col.align } : undefined),
										...(col.variant === 'monoAccent'
											? { fontFamily: 'var(--font-mono)', color: 'var(--color-accent)' }
											: col.variant === 'muted'
												? { color: 'var(--color-text-muted)' }
												: undefined),
									}}
								>
									{col.render(row, i)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
DataTable.displayName = 'DataTable';

export interface CellTitleSubProps {
	/** Riga principale, in grassetto. */
	title: React.ReactNode;
	/** Riga secondaria, attenuata e più piccola. */
	sub?: React.ReactNode;
}

/** Cella di tabella su due righe: riga principale in grassetto + riga secondaria attenuata. */
const CellTitleSub = ({ title, sub }: CellTitleSubProps) => (
	<>
		<strong>{title}</strong>
		{sub != null && (
			<>
				<br />
				<span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{sub}</span>
			</>
		)}
	</>
);
CellTitleSub.displayName = 'CellTitleSub';

export interface DashPanelProps {
	/** Contenuto del pannello (tipicamente una o più {@link DashGrid}). */
	children: React.ReactNode;
}

/** Contesto container-query per un pannello dashboard; le griglie al suo interno rispondono alla larghezza del pannello. */
const DashPanel = ({ children }: DashPanelProps) => <div className="@container">{children}</div>;
DashPanel.displayName = 'DashPanel';

export interface DashSectionProps {
	/**
	 * Titolo della sezione. Omesso, resta il solo ritmo verticale: il mock v11
	 * separa le righe di card con lo spazio, non con un'intestazione, e una
	 * sezione senza nome è comunque una sezione.
	 */
	title?: React.ReactNode;
	/** Nome della sezione per gli screen reader, quando non c'è un titolo visibile. */
	ariaLabel?: string;
	/** Contenuto della sezione. */
	children: React.ReactNode;
}

/** Sezione della dashboard, con o senza titolo (legacy `dash-section`). */
const DashSection = ({ title, ariaLabel, children }: DashSectionProps) => (
	<section className="dash-section" aria-label={title == null ? ariaLabel : undefined}>
		{title != null && <h2 className="dash-section-title">{title}</h2>}
		{children}
	</section>
);
DashSection.displayName = 'DashSection';

const DASH_GRID_LAYOUT = {
	/** Three equal KPI cards: prodotta, condivisa, incentivabile. */
	kpi: '@xl:grid-cols-2 @4xl:grid-cols-3',
	/** Main chart + side panel. */
	charts: '@4xl:grid-cols-[2fr_1fr]',
	/** Three equal bottom cards. */
	bottom: '@2xl:grid-cols-2 @4xl:grid-cols-3',
	/** Due card in fondo, quando la terza (solo admin) manca. */
	bottomPair: '@2xl:grid-cols-2',
	/** Riga a piena larghezza (il `g1` del mock). */
	full: '',
	/** Due colonne pari, la griglia «Panoramica» di più configurazioni (`repeat(2,1fr)` del mock). */
	configs: '@2xl:grid-cols-2 gap-3.5',
} as const;

export interface DashGridProps {
	/** Preset di layout responsive (numero/proporzione di colonne) da applicare alla riga. */
	layout: keyof typeof DASH_GRID_LAYOUT;
	/** Card della riga. */
	children: React.ReactNode;
}

/** Griglia responsive a container-query per le righe della dashboard. */
const DashGrid = ({ layout, children }: DashGridProps) => (
	<div className={cn('grid grid-cols-1 gap-5', DASH_GRID_LAYOUT[layout])}>{children}</div>
);
DashGrid.displayName = 'DashGrid';

export interface DashGridHeroProps {
	/** Contenuto della card hero. */
	children: React.ReactNode;
}

/** Slot per la card "hero" dentro un {@link DashGrid} con `layout="kpi"`: occupa tutta la riga finché la griglia non guadagna una terza colonna. */
const DashGridHero = ({ children }: DashGridHeroProps) => (
	<div className="@xl:col-span-2 @4xl:col-span-1 [&>.card]:h-full">{children}</div>
);
DashGridHero.displayName = 'DashGridHero';

export interface ForecastRowProps {
	/** Etichetta del giorno (es. "Lun"). */
	day: React.ReactNode;
	/** Larghezza della barra, 0-100; 0 renderizza una barra vuota. */
	pct: number;
	/** Valore in kWh mostrato a destra. */
	kwhLabel: React.ReactNode;
	/** Etichetta di confidenza della previsione. */
	confLabel: React.ReactNode;
	/** Se true, non mostra il riempimento della barra (giorno senza dati). */
	empty?: boolean;
	/** Colore del riempimento: `default` accento, `low` grigio (bassa confidenza), `peak` gradiente verde. Default: `default`. */
	fill?: 'default' | 'low' | 'peak';
	/** Se `success`, evidenzia l'etichetta del giorno (giorno di picco). */
	dayTone?: 'success';
	/** Colore dell'etichetta di confidenza. Default: `muted`. */
	confTone?: 'success' | 'warn' | 'muted';
}

export interface ForecastHeaderProps {
	/** Le 4 intestazioni di colonna (giorno, produzione, kWh, confidenza). Default: `['GG', 'Produzione', 'kWh', 'Conf.']`. */
	labels?: [React.ReactNode, React.ReactNode, React.ReactNode, React.ReactNode];
}

/** Intestazioni di colonna per l'elenco delle previsioni a 7 giorni. */
const ForecastHeader = ({ labels = ['GG', 'Produzione', 'kWh', 'Conf.'] }: ForecastHeaderProps) => (
	<div className="mb-1 flex items-center gap-2 text-[12px] text-text-muted font-mono uppercase tracking-wide">
		<span className="w-7">{labels[0]}</span>
		<span className="flex-1">{labels[1]}</span>
		<span className="w-12 text-right">{labels[2]}</span>
		<span className="w-8 text-right">{labels[3]}</span>
	</div>
);
ForecastHeader.displayName = 'ForecastHeader';

const FORECAST_FILL: Record<NonNullable<ForecastRowProps['fill']>, string | undefined> = {
	default: undefined,
	low: 'linear-gradient(90deg,#64748b,rgba(100,116,139,.3))',
	peak: 'linear-gradient(90deg,var(--color-accent-green),var(--color-accent))',
};

const FORECAST_CONF_COLOR: Record<NonNullable<ForecastRowProps['confTone']>, string> = {
	success: 'var(--color-accent-green)',
	warn: 'var(--color-warn)',
	muted: 'var(--color-text-muted)',
};

/** Un giorno della previsione di produzione a 7 giorni (legacy `forecast-row`). */
const ForecastRow = ({
	day,
	pct,
	kwhLabel,
	confLabel,
	empty,
	fill = 'default',
	dayTone,
	confTone = 'muted',
}: ForecastRowProps) => (
	<div className="forecast-row">
		<span
			className="forecast-day"
			style={dayTone === 'success' ? { color: 'var(--color-accent-green)' } : undefined}
		>
			{day}
		</span>
		<div className="forecast-bar" style={{ background: empty ? 'transparent' : undefined }}>
			{!empty && (
				<div
					className="forecast-fill"
					style={{
						width: `${Math.max(0, Math.min(100, pct))}%`,
						background: FORECAST_FILL[fill],
					}}
				/>
			)}
		</div>
		<span className="forecast-val" style={{ color: empty ? 'var(--color-text-muted)' : undefined }}>
			{kwhLabel}
		</span>
		<span className="forecast-conf" style={{ color: FORECAST_CONF_COLOR[confTone] }}>
			{confLabel}
		</span>
	</div>
);
ForecastRow.displayName = 'ForecastRow';

export interface DistributionSegment {
	/** Colore del segmento. */
	tone: WidgetTone;
	/** Percentuale occupata dal segmento (0-100). */
	pct: number;
	/** Tooltip nativo mostrato al passaggio del mouse. */
	title?: string;
}

export interface DistributionBarProps {
	/** Segmenti che compongono la barra (es. stati di iscrizione soci). */
	segments: DistributionSegment[];
	/** Nome accessibile della barra, letto dagli screen reader al posto del contenuto grafico. */
	ariaLabel: string;
}

/** Barra di distribuzione orizzontale a segmenti impilati (es. stati di iscrizione). */
const DistributionBar = ({ segments, ariaLabel }: DistributionBarProps) => (
	<div
		className="mb-3 flex h-2 w-full overflow-hidden rounded-full"
		style={{ background: 'var(--color-border)' }}
		// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- CSS-rendered distribution bar (no raster source); the role + aria-label is the accessible pattern
		role="img"
		aria-label={ariaLabel}
	>
		{segments.map(
			(s, i) =>
				s.pct > 0 && (
					<div
						key={i}
						style={{ width: `${s.pct}%`, background: TONE_VAR[s.tone] }}
						title={s.title}
					/>
				),
		)}
	</div>
);
DistributionBar.displayName = 'DistributionBar';

export interface TintedStatTileProps {
	/** Colore del riquadro e del conteggio. */
	tone: WidgetTone;
	/** Valore numerico mostrato in grande. */
	count: React.ReactNode;
	/** Etichetta sotto il conteggio. */
	label: React.ReactNode;
}

/** Riquadro con conteggio colorato dal proprio tono (statistiche di iscrizione). */
const TintedStatTile = ({ tone, count, label }: TintedStatTileProps) => (
	<div
		className="flex flex-col items-center rounded-lg border border-border p-2"
		style={{ background: `color-mix(in srgb, ${TONE_VAR[tone]} 8%, transparent)` }}
	>
		<span className="font-mono text-xl font-bold" style={{ color: TONE_VAR[tone] }}>
			{count}
		</span>
		<span className="mt-0.5 text-center text-[12px] text-text-muted">{label}</span>
	</div>
);
TintedStatTile.displayName = 'TintedStatTile';

export interface MemberRowItemProps {
	/** Iniziali mostrate nell'avatar. */
	initials: React.ReactNode;
	/** Colore dell'avatar. Default: `accent`. */
	avatarTone?: WidgetTone;
	/** Nome del socio. */
	title: React.ReactNode;
	/** Testo secondario (es. ruolo, pod). */
	subtitle?: React.ReactNode;
	/** Valore energetico mostrato a destra. */
	kwhLabel?: React.ReactNode;
	/** Valore dell'incentivo mostrato a destra. */
	incentiveLabel?: React.ReactNode;
}

/** Riga compatta di un elenco soci, con avatar colorato (legacy `member-row`). */
const MemberRowItem = ({
	initials,
	avatarTone = 'accent',
	title,
	subtitle,
	kwhLabel,
	incentiveLabel,
}: MemberRowItemProps) => (
	<div className="member-row">
		<div
			className="member-avatar"
			style={{
				background: `color-mix(in srgb, ${TONE_VAR[avatarTone]} 20%, transparent)`,
				color: TONE_VAR[avatarTone],
			}}
		>
			{initials}
		</div>
		{/* Corpi e pesi sono quelli del mock: il nome eredita i 13px della riga senza
		    grassetto, la qualifica sotto sta a 10px, e i due valori restano alla
		    misura della riga — così a saltare all'occhio sono i numeri, non i nomi. */}
		<div className="min-w-0 flex-1">
			<div className="truncate text-text-primary">{title}</div>
			{subtitle && <div className="text-[10px] text-text-muted">{subtitle}</div>}
		</div>
		{kwhLabel != null && <span className="member-kwh">{kwhLabel}</span>}
		{incentiveLabel != null && <span className="member-incentive">{incentiveLabel}</span>}
	</div>
);
MemberRowItem.displayName = 'MemberRowItem';

export interface EnergyBalanceRowProps {
	/** Colore del pallino e del valore. */
	tone: WidgetTone;
	/** Etichetta della voce del bilancio energetico. */
	label: React.ReactNode;
	/** Valore numerico della voce. */
	value: React.ReactNode;
}

/** Riga etichetta/valore con pallino colorato (dettaglio del bilancio energetico). */
const EnergyBalanceRow = ({ tone, label, value }: EnergyBalanceRowProps) => (
	<div className="flex items-center gap-2 text-[13px]">
		<div className="h-2 w-2 rounded-full" style={{ background: TONE_VAR[tone] }} />
		<span className="flex-1 text-text-muted">{label}</span>
		<span className="font-mono font-bold" style={{ color: TONE_VAR[tone] }}>
			{value}
		</span>
	</div>
);
EnergyBalanceRow.displayName = 'EnergyBalanceRow';

export interface HighlightBoxProps {
	/** Colore del box. Default: `success`. */
	tone?: WidgetTone;
	/** Icona mostrata prima del contenuto. */
	icon?: React.ReactNode;
	/** Contenuto principale (riga con icona). */
	children: React.ReactNode;
	/** Riga secondaria attenuata sotto il contenuto. */
	sub?: React.ReactNode;
}

/** Box colorato in evidenza con riga a icona e riga secondaria attenuata. */
const HighlightBox = ({ tone = 'success', icon, children, sub }: HighlightBoxProps) => (
	<div
		className="mt-2.5 rounded-lg border p-2"
		style={{
			background: `color-mix(in srgb, ${TONE_VAR[tone]} 10%, transparent)`,
			borderColor: `color-mix(in srgb, ${TONE_VAR[tone]} 20%, transparent)`,
		}}
	>
		<div className="flex items-center gap-1.5 text-[13px] [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:shrink-0">
			{icon && (
				<span className="inline-flex [&_svg]:h-3.5 [&_svg]:w-3.5" style={{ color: TONE_VAR[tone] }}>
					{icon}
				</span>
			)}
			{children}
		</div>
		{sub && <div className="mt-0.5 text-[12px] text-text-muted">{sub}</div>}
	</div>
);
HighlightBox.displayName = 'HighlightBox';

export interface IconNoteGroupProps {
	/** Righe {@link IconNote} da elencare. */
	children: React.ReactNode;
}

/** Elenco di note con icona, attenuate, separate da un bordo superiore, sotto una card. */
const IconNoteGroup = ({ children }: IconNoteGroupProps) => (
	<div className="mt-2 space-y-1 border-t border-border pt-2 text-[12px] text-text-muted">
		{children}
	</div>
);
IconNoteGroup.displayName = 'IconNoteGroup';

export interface IconNoteProps {
	/** Icona della nota. */
	icon: React.ReactNode;
	/** Testo della nota. */
	children: React.ReactNode;
}

/** Singola nota con icona dentro {@link IconNoteGroup}. */
const IconNote = ({ icon, children }: IconNoteProps) => (
	<div className="flex items-center gap-1.5 [&_svg]:h-3 [&_svg]:w-3 [&_svg]:shrink-0">
		{icon}
		{children}
	</div>
);
IconNote.displayName = 'IconNote';

export interface SwatchLegendItem {
	/** Colore del quadratino. */
	tone: 'accent' | 'purple' | 'success' | 'warn';
	/** Contenuto (anche ricco) della voce di legenda. */
	content: React.ReactNode;
}

const SWATCH_BG: Record<SwatchLegendItem['tone'], string> = {
	accent: 'bg-accent/60',
	purple: 'bg-accent-purple/60',
	success: 'bg-accent-green/60',
	warn: 'bg-warn/60',
};

export interface SwatchLegendProps {
	/** Voci della legenda. */
	items: SwatchLegendItem[];
}

/** Legenda inline a quadratini colorati, con contenuto ricco per voce. */
const SwatchLegend = ({ items }: SwatchLegendProps) => (
	<div className="mt-2 flex items-center gap-4 text-[12px] text-text-muted">
		{items.map((item, i) => (
			<span key={i} className="flex items-center gap-1.5">
				<span className={cn('inline-block h-2 w-3 rounded-sm', SWATCH_BG[item.tone])} />
				{item.content}
			</span>
		))}
	</div>
);
SwatchLegend.displayName = 'SwatchLegend';

export {
	ActivityItem,
	AiBox,
	BalanceLine,
	BalanceList,
	CardGrid,
	CellTitleSub,
	ChartLegend,
	DashGrid,
	DashGridHero,
	DashPanel,
	DashSection,
	DataTable,
	DistributionBar,
	EnergyBalanceRow,
	Footnote,
	ForecastHeader,
	ForecastRow,
	GseChecklistItem,
	HighlightBox,
	IconNote,
	IconNoteGroup,
	InfoStrip,
	InlineProgressRow,
	KpiBig,
	MemberRowItem,
	MicroLabel,
	MiniBar,
	ProgressItem,
	QueueRow,
	ReportCardRow,
	SectionHead,
	SwatchLegend,
	Tag,
	TintedStatTile,
};
